import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from './AuthContext';
import { useMultiHOA } from './MultiHOAContext';
import api from './api';

const SupportTicketSystem = () => {
  const { user } = useContext(AuthContext);
  const { activeHoaId } = useMultiHOA();
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [newTicket, setNewTicket] = useState({
    subject: '',
    description: '',
    priority: 'medium',
    category: 'general',
    document_id: '',
    document_name: ''
  });
  const [searchTicketNumber, setSearchTicketNumber] = useState('');
  const [expandedTickets, setExpandedTickets] = useState(new Set());

  const toggleTicketExpanded = (ticketId) => {
    setExpandedTickets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(ticketId)) {
        newSet.delete(ticketId);
      } else {
        newSet.add(ticketId);
      }
      return newSet;
    });
  };

  useEffect(() => {
    if (user) {
      fetchUserTickets();
    }
    
    // Check if we're coming from a document removal request
    const docId = sessionStorage.getItem('supportTicketDocId');
    const docName = sessionStorage.getItem('supportTicketDocName');
    if (docId) {
      setNewTicket({
        ...newTicket,
        category: 'document_removal',
        document_id: docId,
        document_name: docName || '',
        subject: 'Request to remove document',
        priority: 'medium'
      });
      setShowCreateTicket(true);
      // Clear session storage
      sessionStorage.removeItem('supportTicketDocId');
      sessionStorage.removeItem('supportTicketDocName');
    }
  }, [user]);

  const fetchUserTickets = async () => {
    try {
      const currentHoaId = activeHoaId || user?.hoa_id;
      if (!currentHoaId) return;
      
      const { data } = await api.get(`/api/support/tickets?hoa_id=${currentHoaId}`);
      setTickets(data.tickets || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    }
  };

  const submitTicket = async () => {
    if (!newTicket.subject.trim() || !newTicket.description.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    // Validate document removal requests
    if (newTicket.category === 'document_removal') {
      if (!newTicket.document_id.trim()) {
        alert('⚠️ Document ID is required for removal requests.\n\nPlease provide the Document ID from the documents list.');
        return;
      }
    }

    try {
      const currentHoaId = activeHoaId || user?.hoa_id;
      if (!currentHoaId) return;
      
      const ticketData = {
        hoa_code: user.hoa_code || 'N/A',
        hoa_id: currentHoaId,
        user_id: user.id,
        user_email: user.email,
        user_name: `${user.first_name} ${user.last_name}`,
        subject: newTicket.subject,
        description: newTicket.description,
        priority: newTicket.priority,
        category: newTicket.category,
        status: 'open',
        created_at: new Date().toISOString(),
        // Include document info if it's a document removal request
        ...(newTicket.category === 'document_removal' && {
          document_id: newTicket.document_id,
          document_name: newTicket.document_name
        })
      };

      const { data: result } = await api.post(`/api/support/tickets`, ticketData);
      
      if (result.success) {
        alert(`✅ Support Ticket Created!\n\nTicket #: ${result.ticket_number}\n\nYour request has been submitted to HOA Manager Pro support. You will receive an email response within 24 hours.\n\nSave your ticket number for future reference.`);
        setNewTicket({ 
          subject: '', 
          description: '', 
          priority: 'medium', 
          category: 'general',
          document_id: '',
          document_name: ''
        });
        setShowCreateTicket(false);
        fetchUserTickets();
      } else {
        alert(`❌ Failed to create ticket: ${result.message}`);
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      alert('❌ Error creating ticket. Please try again.');
    }
  };

  const searchExistingTicket = async () => {
    if (!searchTicketNumber.trim()) {
      alert('Please enter a ticket number');
      return;
    }

    try {
      const currentHoaId = activeHoaId || user?.hoa_id;
      if (!currentHoaId) return;
      
      const { data } = await api.get(`/api/support/tickets/${searchTicketNumber}?hoa_id=${currentHoaId}`);
      
      if (data.ticket) {
        alert(`📋 Ticket #${searchTicketNumber}\n\nStatus: ${data.ticket.status.toUpperCase()}\nSubject: ${data.ticket.subject}\nCreated: ${new Date(data.ticket.created_at).toLocaleDateString()}\n\nLatest Update: ${data.ticket.last_response || 'No updates yet'}`);
      } else {
        alert('❌ Ticket not found or access denied');
      }
    } catch (error) {
      console.error('Error searching ticket:', error);
      alert('❌ Error searching ticket. Please try again.');
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'open': return 'text-red-600 bg-red-100';
      case 'in_progress': return 'text-yellow-600 bg-yellow-100';
      case 'resolved': return 'text-green-600 bg-green-100';
      case 'closed': return 'text-gray-600 bg-gray-100';
      default: return 'text-blue-600 bg-blue-100';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high': return 'text-red-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'document_removal': return '🗑️ Document Removal';
      case 'technical': return '🔧 Technical Issue';
      case 'billing': return '💳 Billing';
      case 'feature_request': return '✨ Feature Request';
      case 'general': return '💬 General';
      default: return '💬 General';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Contact HOA Manager Pro Support</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get help with your HOA Manager Pro platform, including document removal requests
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowCreateTicket(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              🎫 Create New Ticket
            </button>
          </div>
        </div>
      </div>

      {/* Search Existing Ticket */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h4 className="text-md font-medium text-gray-900 mb-4">Follow Up on Existing Ticket</h4>
        <div className="flex space-x-3">
          <input
            type="text"
            placeholder="Enter ticket number (e.g., TK-2024-001)"
            value={searchTicketNumber}
            onChange={(e) => setSearchTicketNumber(e.target.value)}
            className="flex-1 border border-gray-300 rounded-md px-3 py-2"
          />
          <button
            onClick={searchExistingTicket}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            🔍 Search
          </button>
        </div>
      </div>

      {/* User Tickets */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <h4 className="text-md font-medium text-gray-900">Your Support Tickets</h4>
        </div>
        <div className="p-6">
          {tickets.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No tickets found</p>
              <p className="text-sm text-gray-400 mt-1">Create a ticket to get support</p>
            </div>
          ) : (
            <div className="space-y-4">
              {tickets.map((ticket) => (
                <div key={ticket.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="font-mono text-sm text-blue-600">#{ticket.ticket_number}</span>
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(ticket.status)}`}>
                          {ticket.status?.toUpperCase()}
                        </span>
                        {ticket.category && (
                          <span className="text-xs text-gray-600">
                            {getCategoryLabel(ticket.category)}
                          </span>
                        )}
                      </div>
                      <h5 className="font-medium text-gray-900 mt-2">{ticket.subject}</h5>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{ticket.description}</p>
                      {ticket.document_id && (
                        <div className="mt-2 text-xs text-gray-500 bg-gray-50 p-2 rounded">
                          <span className="font-semibold">Document ID:</span> {ticket.document_id}
                          {ticket.document_name && (
                            <span className="ml-2">• <span className="font-semibold">Name:</span> {ticket.document_name}</span>
                          )}
                        </div>
                      )}
                      <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                        <span className={getPriorityColor(ticket.priority)}>
                          Priority: {ticket.priority?.toUpperCase()}
                        </span>
                        <span>Created: {new Date(ticket.created_at).toLocaleDateString()}</span>
                      </div>
                      
                      {/* View Responses Button - Always show */}
                      <button
                        onClick={() => toggleTicketExpanded(ticket.id)}
                        className="mt-3 text-sm text-blue-600 hover:text-blue-800 font-medium"
                      >
                        {expandedTickets.has(ticket.id) ? '▼ Hide Details' : '▶ View Details & Responses'}
                        {ticket.responses && ticket.responses.length > 0 && ` (${ticket.responses.length})`}
                      </button>
                      
                      {/* Expanded Responses Section */}
                      {expandedTickets.has(ticket.id) && (
                        <div className="mt-3 space-y-2 border-t pt-3">
                          {ticket.responses && ticket.responses.length > 0 ? (
                            <>
                              <p className="text-xs font-semibold text-gray-700 mb-2">Response History:</p>
                              {ticket.responses.map((response, index) => (
                                <div key={response.id || index} className="bg-blue-50 p-3 rounded-md">
                                  <div className="flex justify-between items-start mb-1">
                                    <span className="text-xs font-medium text-gray-700">
                                      {response.employee_name || 'Support Staff'}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {new Date(response.created_at).toLocaleString()}
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{response.response_text}</p>
                                </div>
                              ))}
                            </>
                          ) : (
                            <div className="text-sm text-gray-500 italic p-3 bg-gray-50 rounded">
                              No responses yet. Your ticket is being reviewed by our support team.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Ticket Modal */}
      {showCreateTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80]">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Create Support Ticket</h3>
                <button
                  onClick={() => setShowCreateTicket(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>HOA Code:</strong> {user.hoa_code || 'Auto-populated'}
                  </p>
                  <p className="text-sm text-blue-600 mt-1">
                    This will be automatically included in your ticket for faster support
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  <select
                    value={newTicket.category}
                    onChange={(e) => setNewTicket({...newTicket, category: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="general">💬 General Support</option>
                    <option value="document_removal">🗑️ Document Removal Request</option>
                    <option value="technical">🔧 Technical Issue</option>
                    <option value="billing">💳 Billing Question</option>
                    <option value="feature_request">✨ Feature Request</option>
                  </select>
                </div>

                {newTicket.category === 'document_removal' && (
                  <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                    <h4 className="font-medium text-yellow-800 mb-2">📋 Document Removal Request</h4>
                    <p className="text-sm text-yellow-700 mb-3">
                      To request removal of a document that has passed the draft period, please provide:
                    </p>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Document ID * <span className="text-xs text-gray-500">(found in Documents tab)</span>
                        </label>
                        <input
                          type="text"
                          placeholder="e.g., doc-abc123"
                          value={newTicket.document_id}
                          onChange={(e) => setNewTicket({...newTicket, document_id: e.target.value})}
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Document Name <span className="text-xs text-gray-500">(optional, helps us identify it)</span>
                        </label>
                        <input
                          type="text"
                          placeholder="e.g., Annual Budget 2024"
                          value={newTicket.document_name}
                          onChange={(e) => setNewTicket({...newTicket, document_name: e.target.value})}
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority Level
                  </label>
                  <select
                    value={newTicket.priority}
                    onChange={(e) => setNewTicket({...newTicket, priority: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="low">Low - General question</option>
                    <option value="medium">Medium - Feature request or standard issue</option>
                    <option value="high">High - Urgent system issue</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject *
                  </label>
                  <input
                    type="text"
                    placeholder={newTicket.category === 'document_removal' ? 'Request to remove document' : 'Brief description of your issue'}
                    value={newTicket.subject}
                    onChange={(e) => setNewTicket({...newTicket, subject: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {newTicket.category === 'document_removal' ? 'Reason for Removal *' : 'Describe Your Concern *'}
                  </label>
                  <textarea
                    rows={6}
                    placeholder={
                      newTicket.category === 'document_removal' 
                        ? 'Please provide a detailed explanation for why this document should be removed (e.g., uploaded in error, contains incorrect information, legal compliance, etc.)'
                        : 'Please provide detailed information about your issue or question...'
                    }
                    value={newTicket.description}
                    onChange={(e) => setNewTicket({...newTicket, description: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {newTicket.category === 'document_removal' 
                      ? 'Provide specific details about why this document needs to be removed from the system'
                      : 'Include any error messages, steps to reproduce the issue, or specific questions'}
                  </p>
                </div>

                <div className="bg-yellow-50 p-3 rounded-lg">
                  <h4 className="font-medium text-yellow-800 mb-2">📞 Support Response Times</h4>
                  <ul className="text-sm text-yellow-700 space-y-1">
                    <li>• <strong>High Priority:</strong> Response within 4 hours</li>
                    <li>• <strong>Medium Priority:</strong> Response within 24 hours</li>
                    <li>• <strong>Low Priority:</strong> Response within 48 hours</li>
                  </ul>
                  {newTicket.category === 'document_removal' && (
                    <p className="text-xs text-yellow-600 mt-2 font-medium">
                      ⚠️ Document removal requests require administrator review for compliance with legal retention requirements.
                    </p>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowCreateTicket(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={submitTicket}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                >
                  🎫 Submit Ticket
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportTicketSystem;